import { useCallback, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { requireAuth } from "@/lib/auth";
import { useResume } from "@/components/GoogleReturn";
import { TestFunding } from "@/components/proposal/TestFunding";

export const Support = ({ p, onChange }) => {
  const s = p.support;
  const [amount, setAmount] = useState(s.mine?.amount ?? "");
  const [cond, setCond] = useState(s.mine?.conditions || s.defaultCondition);
  const [anon, setAnon] = useState(!!s.mine?.anonymous);
  const [busy, setBusy] = useState(false);
  const closed = p.ownerStatus === "Declined by owner";
  useResume(`pledge:${p.id}`, useCallback(() => document.querySelector('[data-testid="pledge-amount-input"]')?.focus(), []));

  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); onChange(data); toast(ok); } catch (e) { toast(errMsg(e, "Something went wrong. Try again.")); }
    setBusy(false);
  };
  const pledge = () => {
    const n = Number(amount);
    if (!(n >= 1 && n <= 100000)) return toast("Enter an amount between $1 and $100,000.");
    requireAuth("Pledge support", () => run(() => api.post(`/proposals/${p.id}/pledges`, { amount: n, conditions: cond, anonymous: anon }), "Conditional pledge saved"), `pledge:${p.id}`);
  };

  return (
    <section className="rounded-xl bg-[var(--charcoal)] p-6 text-[var(--bone)] sm:p-8" data-testid="support-section">
      <div className="eyebrow !text-[var(--bone)]/60">Conditional support · optional upgrades only</div>
      <p className="mt-3 font-display text-4xl tracking-tight sm:text-5xl" data-testid="support-total">${s.total.toLocaleString()}</p>
      <p className="mt-2 text-sm text-[var(--bone)]/75" data-testid="support-label">{s.label}</p>
      <div className="mt-2 text-sm font-semibold tabular-nums tracking-normal text-[var(--bone)]/85" style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif" }} data-testid="funds-collected">Funds collected: $0 · {p.fundingStatus}</div>
      <p className="mt-2 text-xs text-[var(--bone)]/60" data-testid="platform-fee-disclosure">PlaceBacker charges a transparent 5% platform fee only on successfully collected funds.</p>
      {s.supporters.length > 0 && (
        <ul className="mt-5 grid gap-1.5 border-t border-white/15 pt-4 text-sm" data-testid="supporters-list">
          {s.supporters.map((x, i) => (
            <li key={i} className="flex justify-between gap-3"><span>{x.name}</span><span className="text-[var(--bone)]/70">${x.amount.toLocaleString()} · {x.status}</span></li>
          ))}
        </ul>
      )}
      <TestFunding p={p} onChange={onChange} />
      {!closed && (
        <div className="mt-6 grid gap-3 border-t border-white/15 pt-5">
          <label className="flex items-center gap-2 rounded-xl bg-white/10 px-3">
            <span className="text-lg">$</span>
            <input type="number" min={1} max={100000} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount you'd contribute" className="h-12 w-full bg-transparent text-base outline-none placeholder:text-white/40" data-testid="pledge-amount-input" />
          </label>
          <textarea value={cond} onChange={(e) => setCond(e.target.value)} rows={3} className="w-full rounded-xl bg-white/10 p-3 text-sm outline-none" data-testid="pledge-conditions-input" />
          <label className="flex min-h-[40px] items-center gap-2 text-sm"><input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="pledge-anonymous-checkbox" /> Show me as Anonymous</label>
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary !bg-[var(--bone)] !text-[var(--charcoal)] hover:!bg-white" disabled={busy} onClick={pledge} data-testid="pledge-submit-button">{s.mine ? "Update my pledge" : "Pledge conditional support"}</button>
            {s.mine && <button className="btn-quiet !text-[var(--bone)]/80" disabled={busy} onClick={() => run(() => api.delete(`/proposals/${p.id}/pledges/mine`), "Pledge withdrawn")} data-testid="pledge-withdraw-button">Withdraw</button>}
          </div>
          <p className="text-xs text-[var(--bone)]/55">A nonbinding IOU. No payment is taken. It never applies to required repairs.</p>
        </div>
      )}
    </section>
  );
};
